import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const location = useLocation()

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false)
    setDropdownOpen(false)
  }, [location])

  const navItems = [
    { label: 'Forside', path: '/' },
    { label: 'Om Birgitte', path: '/om-birgitte' },
    { label: 'Erfaring', path: '/erfaring' },
    { label: 'Referencer', path: '/referencer' },
    { label: 'Galleri', path: '/galleri' },
    { label: 'Kontakt', path: '/kontakt' },
  ]

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop 2-row layout (1200px and above) */}
        <div className="hidden min-[1200px]:block">
          {/* Top row: Logo on left, Contact info & CTA on right */}
          <div className="flex items-center justify-between py-4 border-b border-slate-100">
            <Link to="/" className="flex items-center group focus:outline-none">
              <div className="flex flex-col">
                <span className="text-xl sm:text-2xl font-black tracking-tight text-black group-hover:text-sky-600 transition-colors">
                  Insightly Care
                </span>
                <span className="text-[10px] uppercase font-bold tracking-widest text-black opacity-80">
                  IT & Tech Recruitment
                </span>
              </div>
            </Link>

            <div className="flex items-center gap-4">
              <a
                href="tel:28839488"
                className="text-xs font-semibold text-black hover:opacity-75 flex items-center gap-2 whitespace-nowrap px-3 py-2 bg-slate-100 transition-colors border border-slate-200"
              >
                <i className="fa-solid fa-phone text-black" aria-hidden="true" />
                <span className="whitespace-nowrap font-bold text-black">28 83 94 88</span>
              </a>
              <Link
                to="/kontakt"
                className="px-5 py-2.5 text-sm font-bold text-white bg-sky-600 hover:bg-sky-700 transition-all hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
              >
                Book en samtale
              </Link>
            </div>
          </div>

          {/* Bottom row: Menu links placed under logo, left-aligned */}
          <div className="py-2.5 flex items-center justify-start">
            <nav className="flex items-center gap-1 -ml-3">
              {navItems.map((item) => {
                const active = isActive(item.path)
                if (item.hasDropdown) {
                  return (
                    <div key={item.path} className="relative" onMouseLeave={() => setDropdownOpen(false)}>
                      <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        onMouseEnter={() => setDropdownOpen(true)}
                        className={`px-3 py-2 text-sm font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                          active
                            ? 'text-black font-black'
                            : 'text-black font-medium hover:opacity-75'
                        }`}
                      >
                        {item.label}
                        <i
                          className={`fa-solid fa-chevron-down text-xs transition-transform ${
                            dropdownOpen ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </button>

                      {dropdownOpen && (
                        <div className="absolute left-0 mt-1 w-64 bg-white border border-slate-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                          <Link
                            to={item.path}
                            className="block px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-black hover:bg-slate-100"
                          >
                            Oversigt: IT & Tech
                          </Link>
                          <div className="h-px bg-slate-100 my-1" />
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className="block px-4 py-2 text-sm text-black hover:bg-slate-100"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3 py-2 text-sm font-semibold transition-all ${
                      active
                        ? 'text-black font-black'
                        : 'text-black font-medium hover:opacity-75'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>
          </div>
        </div>

        {/* Mobile / Tablet header bar (Below 1200px) */}
        <div className="flex min-[1200px]:hidden items-center justify-between h-20">
          <Link to="/" className="flex items-center group focus:outline-none">
            <div className="flex flex-col">
              <span className="text-xl font-black tracking-tight text-black group-hover:text-sky-600 transition-colors">
                Insightly Care
              </span>
              <span className="text-[10px] uppercase font-bold tracking-widest text-black opacity-80">
                IT & Tech Recruitment
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <a
              href="tel:28839488"
              className="text-xs font-semibold text-black bg-slate-100 px-3 py-1.5 whitespace-nowrap flex items-center gap-1 border border-slate-200"
            >
              <i className="fa-solid fa-phone text-xs text-black" aria-hidden="true" />
              <span className="whitespace-nowrap font-bold text-black">28 83 94 88</span>
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2.5 text-black focus:outline-none cursor-pointer"
              aria-label="Åbn navigation"
            >
              <i
                className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-xl text-black`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet Menu overlay (Below 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-slate-200 px-4 pt-2 pb-6 space-y-2 animate-in slide-in-from-top duration-200 text-left">
          {navItems.map((item) => {
            const active = isActive(item.path)
            return (
              <div key={item.path} className="text-left">
                <Link
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`block text-left px-3 py-2 text-base font-semibold transition-colors ${
                    active ? 'text-black font-black' : 'text-black font-medium hover:opacity-75'
                  }`}
                >
                  {item.label}
                </Link>

                {item.hasDropdown && (
                  <div className="pl-6 space-y-1 mt-1 text-left">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setIsOpen(false)}
                        className="block text-left px-3 py-2 text-sm text-black hover:opacity-75"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          })}

          <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
            <a
              href="tel:28839488"
              className="flex items-center justify-center gap-2 py-3 bg-slate-100 text-black font-bold whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-black" aria-hidden="true" />
              <span className="whitespace-nowrap text-black">Tlf: 28 83 94 88</span>
            </a>
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="w-full text-center py-3 bg-sky-600 text-white font-bold hover:bg-sky-700 transition-colors"
            >
              Book en samtale med Birgitte
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
